import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { Trip } from '@generated/graphql';
import { DateTime } from 'luxon';
import { TripsTripSelfBookingDepartureDetailComponent } from '../trips-trip-self-booking-departure-detail/trips-trip-self-booking-departure-detail.component';

import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DjangoService, Routes } from '@core/services/django.service';
import { EnvironmentService } from '@core/services/environment.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { WINDOW } from '@core/services/window.service';
import { ANALYTICS_EVENTS } from '@core/utils/AnalyticsEvents';
import { ModalService } from '@features/modal/services/modal.service';
import { LeadRequestCreatePolymorphicTypes as LeadRequestType } from '@generated/openapi/model/leadRequestCreatePolymorphicTypes';
import { PublicDeparture } from '@generated/openapi/model/publicDeparture';
import { CtaButtonComponent } from '@shared/cta-button/cta-button.component';
import { CustomCheckboxComponent } from '@shared/custom-checkbox/custom-checkbox.component';
import { EnquireFormModalComponent } from '@shared/enquire-form-modal/enquire-form-modal.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import {
    BehaviorSubject,
    Subject,
    fromEvent,
    map,
    shareReplay,
    takeUntil,
    tap,
} from 'rxjs';
type DepartureBucket = { [key: number]: { [key: number]: PublicDeparture[] } };

export interface DepartureWithDates extends PublicDeparture {
    date: Date;
}

interface DropdownItem<T> {
    label: string;
    value: T;
    [key: string]: any;
}

@Component({
    selector: 'app-trips-trip-self-booking',
    imports: [
        CommonModule,
        TripsTripSelfBookingDepartureDetailComponent,
        ReactiveFormsModule,
        LoaderComponent,
        MatCheckboxModule,
        CustomCheckboxComponent,
        CtaButtonComponent,
    ],
    templateUrl: './trips-trip-self-booking.component.html',
    styleUrls: ['./trips-trip-self-booking.component.css'],
})
export class TripsTripSelfBookingComponent
    implements OnInit, OnDestroy, OnChanges
{
    readonly visibleDepartures = 16;
    readonly defaultSelectionIdealMonthRange: [number, number] = [3, 6];

    @Input() trip: Trip;
    @Input() itineraryPageId: string | null = null;
    years: DepartureBucket = {};
    selectMonthDropdownOpen: boolean = false;
    itineraryDropdownOpen: boolean = false;

    filteredDepartures = new BehaviorSubject<any[]>([]);
    startIndex = new BehaviorSubject<number | null>(0);
    endIndex = new BehaviorSubject<number | null>(null);

    allDepartures = new BehaviorSubject<any[]>([]);

    possibleYearsAndMonths: {
        [key: number]: { index: number; count: number }[];
    } = {};

    itinerariesForTrip = [];
    isDeparturesEmptyForSelectedItinerary = false;
    @ViewChild('toggleButton') toggleButton: ElementRef;
    @ViewChild('dropdownMenu') dropdownMenu: ElementRef;

    @ViewChild('toggleItineraryDropdownButton')
    toggleItineraryDropdownButton: ElementRef;
    @ViewChild('itineraryDropdownMenu') itineraryDropdownMenu: ElementRef;
    @ViewChildren(TripsTripSelfBookingDepartureDetailComponent)
    selfBookingDeparturesElements: QueryList<TripsTripSelfBookingDepartureDetailComponent>;
    today: Date;

    monthNames = [
        { label: 'Jan', value: 'january' },
        { label: 'Feb', value: 'february' },
        { label: 'Mar', value: 'march' },
        { label: 'Apr', value: 'april' },
        { label: 'May', value: 'may' },
        { label: 'Jun', value: 'june' },
        { label: 'Jul', value: 'july' },
        { label: 'Aug', value: 'august' },
        { label: 'Sep', value: 'september' },
        { label: 'Oct', value: 'october' },
        { label: 'Nov', value: 'november' },
        { label: 'Dec', value: 'december' },
    ];

    loadingFilteredDepartures = false;

    ngOnDestroy() {
        this.allDepartures.complete();
    }

    buildItineraries() {
        const arr = this.itinerariesForTrip.map(skill => {
            return this.fb.control(skill.selected);
        });
        return this.fb.array(arr);
    }

    tripForm: FormGroup<{
        itinerary: FormArray;
        selectedMonthYear: FormControl<{ month: string; year: number }>;
    }>;

    createFilterForm() {
        this.tripForm = this.fb.group({
            itinerary: new FormArray([]),
            selectedMonthYear: this.fb.control({
                month: '',
                year: new Date().getFullYear(),
            }),
        });
    }

    private destroy = new Subject<any>();

    constructor(
        private djangoService: DjangoService,
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
        @Inject(WINDOW) private window: Window,
        private modalService: ModalService,
        private eventTrackerService: EventTrackerService,
        private environmentService: EnvironmentService
    ) {
        this.today = new Date();
    }

    transformDeparturesWithSections(month) {
        let currentMonth = month;
        return (filteredDepartures, departure) => {
            const year = departure.date.getFullYear();
            const month = departure.date.getMonth();
            if (currentMonth !== month) {
                for (let i = currentMonth + 1; i < month; i++) {
                    const element = this.monthNames[i];
                    filteredDepartures.push({
                        sectionName: `${element.label} ${year}`,
                        type: 'EMPTY_SECTION',
                        monthValue: element.value,
                        yearValue: year,
                    });
                }
                currentMonth = month;
                filteredDepartures.push({
                    sectionName: `${this.monthNames[month].label} ${year}`,
                    type: 'SECTION_HEADING',
                });
            }
            return [...filteredDepartures, { ...departure }];
        };
    }

    getNewFilteredDepartures(currentMonth, startSliceIndex, endSliceIndex) {
        const itinerariesSelected = this.getItinerariesSelected();

        return this.allDepartures
            .getValue()
            .slice(startSliceIndex, endSliceIndex)
            .filter(departure =>
                itinerariesSelected.includes(departure.itinerary)
            )
            .reduce(this.transformDeparturesWithSections(currentMonth), []);
    }

    trackByFn(index: number, item: PublicDeparture) {
        return item.id;
    }

    showLaterDates() {
        const getMonthIndex = this.monthNames.findIndex(
            ele =>
                ele.value ===
                this.tripForm.controls.selectedMonthYear.value.month
        );
        const currentMonth = getMonthIndex - 1;

        this.filteredDepartures.next(
            this.getNewFilteredDepartures(
                currentMonth,
                this.startIndex.getValue(),
                this.endIndex.getValue() + this.visibleDepartures
            )
        );
        this.endIndex.next(this.endIndex.getValue() + this.visibleDepartures);
    }

    showEarlierDates() {
        if (this.startIndex.getValue() - this.visibleDepartures <= 0) {
            const firstDeparture = this.allDepartures
                .getValue()
                .slice(0, this.endIndex.getValue())[0]
                .date.getMonth();
            this.filteredDepartures.next(
                this.getNewFilteredDepartures(
                    firstDeparture - 1,
                    0,
                    this.endIndex.getValue()
                )
            );

            this.startIndex.next(0);
        } else {
            const firstDepartureInSection = this.allDepartures
                .getValue()
                .slice(
                    this.startIndex.getValue() - this.visibleDepartures,
                    this.endIndex.getValue()
                )[0]
                .date.getMonth();
            this.filteredDepartures.next(
                this.getNewFilteredDepartures(
                    firstDepartureInSection - 1,
                    this.startIndex.getValue() - this.visibleDepartures,
                    this.endIndex.getValue()
                )
            );
            this.startIndex.next(
                this.startIndex.getValue() - this.visibleDepartures
            );
        }
    }

    initializeYear(year) {
        const current = { ...this.possibleYearsAndMonths };
        if (!current[year]) {
            this.possibleYearsAndMonths = {
                ...current,
                [year]: this.monthNames.map((value, index) => ({
                    index,
                    count: 0,
                })),
            };
        }
    }

    populateFilterDropdowns(departures: DepartureWithDates[]) {
        const itinerariesFromDeparturesForDropdown = [
            ...new Map(
                departures.map(item => [
                    item.itinerary_id,
                    {
                        id: item.itinerary_id,
                        label: item.itinerary,
                        selected: this.itineraryPageId
                            ? this.itineraryPageId === item.itinerary_id
                            : true,
                    },
                ])
            ).values(),
        ];
        const selectedItinerary = itinerariesFromDeparturesForDropdown.find(
            itinerary => itinerary.selected
        );
        this.isDeparturesEmptyForSelectedItinerary = !selectedItinerary;
        departures.forEach(departure => {
            const year = departure.date.getFullYear();
            const month = departure.date.getMonth();
            this.initializeYear(year);
            this.possibleYearsAndMonths[year][month].count += 1;
        });
        return itinerariesFromDeparturesForDropdown;
    }

    getDepatures() {
        return this.djangoService
            .drfList<PublicDeparture>(Routes.publicDepartures, 'start_date', {
                trip: this.trip.linkedVirtualOfficeTrip,
            })
            .pipe(
                tap(data => {
                    this.loadingFilteredDepartures = false;
                }),
                map(departures => {
                    return departures.map(departure => {
                        return {
                            ...departure,
                            date: DateTime.fromISO(departure.start_date)
                                .toUTC()
                                .toJSDate(),
                        };
                    });
                }),
                tap(departures => {
                    const currentMonth = 0;
                    this.itinerariesForTrip =
                        this.populateFilterDropdowns(departures);
                    if (this.tripForm) {
                        this.tripForm.controls.itinerary.clear();
                        this.itinerariesForTrip.map(control => {
                            this.tripForm.controls.itinerary.push(
                                new FormControl(control.selected)
                            );
                        });
                    }

                    this.filteredDepartures.next(
                        departures.reduce(
                            this.transformDeparturesWithSections(currentMonth),
                            []
                        )
                    );
                }),
                shareReplay(1)
            );
    }

    getItinerariesSelected(): string[] {
        return this.tripForm.controls.itinerary.value.reduce(
            (acc, curr, index) => {
                if (curr) {
                    acc.push(this.itinerariesForTrip[index].label);
                }
                return acc;
            },
            []
        );
    }

    sliceDeparturesWithEmptySections(selectedMonthYear: {
        month: string;
        year: number;
    }) {
        const { month, year } = selectedMonthYear;
        const selectedMonthIndex = this.monthNames.findIndex(
            ele => ele.value === month
        );
        let indexOfFirstDeprtureWithMatchingValues = this.allDepartures
            .getValue()
            .findIndex(departure => {
                if (departure.date || departure.monthValue) {
                    return (
                        (departure?.date?.getMonth() === selectedMonthIndex &&
                            departure?.date?.getFullYear() === year) ||
                        (departure.monthValue === month &&
                            departure.yearValue === year)
                    );
                }
                return false;
            });

        if (indexOfFirstDeprtureWithMatchingValues === -1) {
            for (let i = selectedMonthIndex - 1; i > 0; i--) {
                indexOfFirstDeprtureWithMatchingValues = this.allDepartures
                    .getValue()
                    .findLastIndex(departure => {
                        if (departure.date) {
                            return (
                                departure?.date?.getMonth() === i &&
                                departure?.date?.getFullYear() === year
                            );
                        }
                        return false;
                    });

                if (indexOfFirstDeprtureWithMatchingValues !== -1) {
                    break;
                }
            }
        }
        return indexOfFirstDeprtureWithMatchingValues;
    }

    subscribeToFormChanges() {
        this.tripForm?.valueChanges
            .pipe(
                map(value => {
                    return {
                        ...value.selectedMonthYear,
                        indexOfFirstDepartureWithMatchingValues:
                            this.sliceDeparturesWithEmptySections(
                                value.selectedMonthYear
                            ),
                        selectedMonthIndex: this.getMonthIndexFromName(
                            value.selectedMonthYear.month
                        ),
                    };
                })
            )
            .subscribe(
                ({
                    indexOfFirstDepartureWithMatchingValues,
                    selectedMonthIndex,
                }) => {
                    this.startIndex.next(
                        indexOfFirstDepartureWithMatchingValues
                    );
                    this.endIndex.next(
                        indexOfFirstDepartureWithMatchingValues +
                            this.visibleDepartures
                    );
                    const currentMonth = selectedMonthIndex - 1;
                    this.filteredDepartures.next(
                        this.getNewFilteredDepartures(
                            currentMonth,
                            indexOfFirstDepartureWithMatchingValues,
                            indexOfFirstDepartureWithMatchingValues +
                                this.visibleDepartures
                        )
                    );
                }
            );
    }

    ngOnInit(): void {
        this.createFilterForm();
        this.subscribeToFormChanges();
        if (this.environmentService.isBrowser()) {
            fromEvent(this.window, 'click')
                .pipe(takeUntil(this.destroy))
                .subscribe(event => {
                    if (
                        this.selectMonthDropdownOpen &&
                        !this.dropdownMenu?.nativeElement.contains(
                            event.target
                        ) &&
                        !this.toggleButton?.nativeElement.contains(event.target)
                    ) {
                        this.selectMonthDropdownOpen = false;
                        this.cd.detectChanges();
                    }
                });

            fromEvent(this.window, 'click')
                .pipe(takeUntil(this.destroy))
                .subscribe(event => {
                    if (
                        this.itineraryDropdownOpen &&
                        !this.itineraryDropdownMenu?.nativeElement.contains(
                            event.target
                        ) &&
                        !this.toggleItineraryDropdownButton?.nativeElement.contains(
                            event.target
                        )
                    ) {
                        this.itineraryDropdownOpen = false;
                        this.cd.detectChanges();
                    }
                });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.trip) {
            this.getDepatures().subscribe(value => {
                this.allDepartures.next(value);
                if (value?.length !== 0) {
                    const [monthLabel, year] =
                        this.selectStartingMonthAndYear(value);

                    this.tripForm.controls.selectedMonthYear.setValue({
                        month: monthLabel,
                        year: year,
                    });
                }
            });
        }
    }

    selectStartingMonthAndYear(
        departures: DepartureWithDates[]
    ): [string, number] {
        /**
         * This function is used to select the starting month for the departure list.
         * It will try to select the month that is closest to the lower bound of
         * defaultSelectionIdealMonthRange, but not more than the upper bound in the future.
         * If no departure is found within the range, it will select the first departure's month.
         */
        const lowerBound = DateTime.now()
            .plus({
                months: this.defaultSelectionIdealMonthRange[0],
            })
            .toJSDate();
        const upperBound = DateTime.now()
            .plus({
                months: this.defaultSelectionIdealMonthRange[1],
            })
            .toJSDate();
        const idealMonth = departures.find(
            departure =>
                departure.date >= lowerBound && departure.date <= upperBound
        );

        if (idealMonth) {
            return [
                this.monthNames[idealMonth.date.getMonth()].value,
                idealMonth.date.getFullYear(),
            ];
        }

        return [
            this.monthNames[departures[0].date.getMonth()].value,
            departures[0].date.getFullYear(),
        ];
    }

    getMonthIndexFromName(monthName) {
        return this.monthNames.findIndex(ele => ele.value === monthName);
    }

    showProposeDateModal() {
        this.eventTrackerService.trackEvent(
            ANALYTICS_EVENTS.TRIP_CUSTOM_TRIP_CTA,
            {
                trip: this.trip.title,
            }
        );

        this.modalService.show(
            EnquireFormModalComponent,
            {
                trip: this.trip,
                typeOfEnquiry: LeadRequestType.CustomDepartureLeadRequest,
                analyticsEventSent: ANALYTICS_EVENTS.TRIP_CUSTOM_ENQUIRY_SENT,
                analyticsEventCompleted:
                    ANALYTICS_EVENTS.ITINERARY_CUSTOM_ENQUIRY_COMPLETED,
                analyticsEventAbandoned:
                    ANALYTICS_EVENTS.ITINERARY_CUSTOM_ENQUIRY_ABANDONED,
                analyticsEventData: {
                    trip: this.trip.title,
                },
            },
            false
        );
    }

    toggleSelectMonthDropdown() {
        this.selectMonthDropdownOpen = !this.selectMonthDropdownOpen;
    }
    toggleItineraryDropdown() {
        this.itineraryDropdownOpen = !this.itineraryDropdownOpen;
    }

    selectMonthAndYearFromDropdown(month, year) {
        this.tripForm.controls.selectedMonthYear.setValue({
            month,
            year,
        });
        this.selectMonthDropdownOpen = false;
    }
}
